import { useEffect, useState } from "react";
import { Mail, Reply, MessageCircle, Copy, ArrowLeft, Check, Loader2, Sparkles, Eye, Zap, Shield, Download, AlertTriangle, Send, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";
import rewardfulLogo from "@/assets/rewardful-logo.png";
import stripePremierPartner from "@/assets/stripe-premier-partner.svg";
import { track } from "@/lib/analytics";



const REWARDFUL_FOOTER = "Generated with Rewardful — launch your affiliate program in minutes: https://www.rewardful.com";

type Templates = {
  intel: {
    product: string;
    category: string;
    commission: string;
    idealAffiliates: string[];
    dealScore: number;
  };
  coldEmail: { subject: string; body: string };
  followUp: { subject: string; body: string; timing?: string };
  linkedIn: { body: string };
  copyNotes: string;
};

const LOADING_STAGES = [
  { label: "Reading your homepage…", ms: 2200 },
  { label: "Identifying your ideal partners…", ms: 2600 },
  { label: "Drafting your templates…", ms: 4000 },
  { label: "Polishing the copy…", ms: 3000 },
];

const EXAMPLE: Templates = {
  intel: {
    product: "Linear",
    category: "B2B SaaS — Project management for software teams",
    commission: "30% recurring for 12 months",
    idealAffiliates: [
      "Engineering leadership newsletter writers (Lenny / Pragmatic Engineer style)",
      "Indie hacker YouTubers reviewing dev productivity tools",
      "Agencies setting up workflows for early-stage startup clients",
    ],
    dealScore: 8,
  },
  coldEmail: {
    subject: "Linear x [their newsletter]",
    body: `Hi [First Name],\n\n[Their newsletter] reaches exactly the people who feel the pain Linear was built for — engineering leads at fast-moving startups rethinking how their team ships.\n\nI run partnerships at Linear (project tracker for software teams, used by Vercel, Ramp, and Cash App). We just opened our affiliate program and your audience is a clean fit.\n\nThe deal: 30% recurring for 12 months on every team you refer. Stripe-based tracking, monthly payouts, no caps.\n\nWorth a 10-minute call to see if it's a fit?\n\n[Your Name]\nLinear partnerships`,
  },
  followUp: {
    subject: "Re: Linear x [their newsletter]",
    body: `Hi [First Name], floating this back up. Happy to send a 2-minute Loom of the affiliate dashboard so you can see how attribution and payouts work before deciding anything. Just say the word.\n\n[Your Name]`,
    timing: "Send 5–7 days after the cold email",
  },
  linkedIn: {
    body: `Hi [First Name] — your audience at [their newsletter / channel] looks like an unusually clean fit for Linear's new affiliate program (30% recurring, 12 months). Open to a quick intro?`,
  },
  copyNotes: `Replace [First Name], [their newsletter / channel], and [Your Name] before sending. The Linear product details, social proof, and commission terms are already locked in — leave those as-is.`,
};

function buildMarkdown(t: Templates): string {
  const lines: string[] = [];
  lines.push(`# Affiliate Outreach — ${t.intel?.product ?? "Your Product"}`, "");
  if (t.intel) {
    lines.push("## Affiliate Intel", "");
    lines.push(`- **Product:** ${t.intel.product}`);
    lines.push(`- **Category:** ${t.intel.category}`);
    lines.push(`- **Commission:** ${t.intel.commission}`);
    lines.push(`- **Deal Score:** ${t.intel.dealScore}/10`);
    lines.push("", "**Ideal Affiliates:**");
    t.intel.idealAffiliates.forEach((a) => lines.push(`- ${a}`));
    lines.push("");
  }
  lines.push("## Cold Email", "", `**Subject:** ${t.coldEmail.subject}`, "", t.coldEmail.body, "");
  lines.push("## Follow-Up", "");
  if (t.followUp.timing) lines.push(`_${t.followUp.timing}_`, "");
  lines.push(`**Subject:** ${t.followUp.subject}`, "", t.followUp.body, "");
  lines.push("## LinkedIn DM", "", t.linkedIn.body, "");
  if (t.copyNotes) lines.push("## Before You Send", "", t.copyNotes, "");
  lines.push("---", "", `_${REWARDFUL_FOOTER}_`);
  return lines.join("\n");
}

function buildPlainText(t: Templates): string {
  return buildMarkdown(t)
    .replace(/^#+\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/^- /gm, "• ");
}

function fileSlug(t: Templates): string {
  const slug = (t.intel?.product ?? "outreach").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return slug || "outreach";
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function exportPdf(t: Templates) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 54;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;
  let y = margin;

  const writeBlock = (text: string, size: number, style: "normal" | "bold" = "normal", gap = 6) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, maxW);
    lines.forEach((ln: string) => {
      if (y + size + 4 > pageH - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(ln, margin, y);
      y += size + 4;
    });
    y += gap;
  };

  // Small Rewardful logo in top-right of first page
  try {
    const logoW = 80;
    const logoH = (92 / 417) * logoW;
    doc.addImage(rewardfulLogo, "PNG", pageW - margin - logoW, margin - logoH / 2, logoW, logoH);
  } catch {
    // ignore if image fails
  }

  writeBlock(`Affiliate Outreach — ${t.intel?.product ?? "Your Product"}`, 18, "bold", 12);
  if (t.intel) {
    writeBlock("Affiliate Intel", 14, "bold", 6);
    writeBlock(`Product: ${t.intel.product}`, 11);
    writeBlock(`Category: ${t.intel.category}`, 11);
    writeBlock(`Commission: ${t.intel.commission}`, 11);
    writeBlock(`Deal Score: ${t.intel.dealScore}/10`, 11);
    writeBlock("Ideal Affiliates:", 11, "bold", 2);
    t.intel.idealAffiliates.forEach((a) => writeBlock(`• ${a}`, 11, "normal", 2));
    y += 6;
  }
  writeBlock("Cold Email", 14, "bold", 6);
  writeBlock(`Subject: ${t.coldEmail.subject}`, 11, "bold");
  writeBlock(t.coldEmail.body, 11);
  writeBlock("Follow-Up", 14, "bold", 6);
  if (t.followUp.timing) writeBlock(t.followUp.timing, 10);
  writeBlock(`Subject: ${t.followUp.subject}`, 11, "bold");
  writeBlock(t.followUp.body, 11);
  writeBlock("LinkedIn DM", 14, "bold", 6);
  writeBlock(t.linkedIn.body, 11);
  if (t.copyNotes) {
    writeBlock("Before You Send", 14, "bold", 6);
    writeBlock(t.copyNotes, 11);
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerLines = doc.splitTextToSize(REWARDFUL_FOOTER, maxW);
    doc.text(footerLines, margin, pageH - margin / 2);
  }

  doc.save(`${fileSlug(t)}-templates.pdf`);
}

type ErrorDetail = {
  code: string;
  message: string;
  requestId?: string;
  url?: string;
  commission?: string;
};



const Index = () => {
  const [url, setUrl] = useState("");
  const [commission, setCommission] = useState("");
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [templates, setTemplates] = useState<Templates | null>(null);
  const [error, setError] = useState<ErrorDetail | null>(null);
  const [showExample, setShowExample] = useState(false);

  // Cycle loading stages
  useEffect(() => {
    if (!loading) {
      setStageIdx(0);
      return;
    }
    let i = 0;
    const tick = () => {
      i = Math.min(i + 1, LOADING_STAGES.length - 1);
      setStageIdx(i);
      if (i < LOADING_STAGES.length - 1) {
        timeout = window.setTimeout(tick, LOADING_STAGES[i].ms);
      }
    };
    let timeout = window.setTimeout(tick, LOADING_STAGES[0].ms);
    return () => window.clearTimeout(timeout);
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side URL sanity check
    let parsedUrl: URL;
    try {
      parsedUrl = new URL(url.trim());
      if (!["http:", "https:"].includes(parsedUrl.protocol)) throw new Error();
      // Require a real-looking hostname: must contain a dot and not be localhost/IP
      const host = parsedUrl.hostname.toLowerCase();
      const looksLikeIp = /^\d+(\.\d+){3}$/.test(host);
      if (!host.includes(".") || host === "localhost" || looksLikeIp) throw new Error();
    } catch {
      setError({
        code: "CLIENT_INVALID_URL",
        message: "Please enter a real URL starting with https:// (e.g. https://yourproduct.com).",
      });
      return;
    }
    if (!commission.trim()) {
      setError({
        code: "CLIENT_COMMISSION_REQUIRED",
        message: "Please enter your commission offer.",
      });
      return;
    }
    if (!consent) {
      setError({
        code: "CLIENT_CONSENT_REQUIRED",
        message: "Please agree to the Terms & Privacy Policy to continue.",
      });
      return;
    }

    setLoading(true);
    track("outreach_tool_generation_started", { url_host: parsedUrl.hostname });

    setTemplates(null);
    setShowExample(false);

    const trimmedUrl = url.trim();
    const trimmedCommission = commission.trim();

    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "generate-outreach",
        { body: { url: trimmedUrl, commission: trimmedCommission } },
      );

      // Edge-function transport-level error: the SDK puts the raw Response in
      // `context` — we must await .json() on it to extract our error payload.
      if (fnError) {
        let code = "EDGE_ERROR";
        let message = fnError.message || "Generation failed";
        let requestId: string | undefined;
        const ctx = (fnError as { context?: unknown }).context;
        let status: number | undefined;

        if (ctx instanceof Response) {
          status = ctx.status;
          try {
            const b = await ctx.clone().json() as { error?: string; code?: string; requestId?: string };
            if (b.code) code = b.code;
            if (b.error) message = b.error;
            if (b.requestId) requestId = b.requestId;
          } catch { /* body wasn't JSON */ }
        } else if (ctx && typeof ctx === "object") {
          // Older SDK shape: { status, body }
          const c = ctx as { status?: number; body?: unknown };
          status = c.status;
          const rawBody = c.body;
          const parsed = typeof rawBody === "string"
            ? (() => { try { return JSON.parse(rawBody); } catch { return null; } })()
            : rawBody;
          if (parsed && typeof parsed === "object") {
            const b = parsed as { error?: string; code?: string; requestId?: string };
            if (b.code) code = b.code;
            if (b.error) message = b.error;
            if (b.requestId) requestId = b.requestId;
          }
        }

        // Final fallback by status if we still couldn't parse a body
        if (code === "EDGE_ERROR" && status) {
          if (status === 429) { code = "RATE_LIMITED"; message = "Too many requests. Please wait a minute and try again."; }
          else if (status === 402) { code = "AI_CREDITS_EXHAUSTED"; message = "AI credits exhausted."; }
          else if (status === 422) { code = "URL_PROBLEM"; message = "We couldn't read that URL. Try a different page or your main marketing homepage."; }
          else if (status >= 500) { code = "SERVER_ERROR"; message = `Server error (${status}). Please try again.`; }
        }
        throw Object.assign(new Error(message), { code, requestId });
      }
      // Edge function returned a 200 with explicit error payload (rare)
      if (data && (data as { error?: string }).error) {
        const d = data as { error: string; code?: string; requestId?: string };
        throw Object.assign(new Error(d.error), { code: d.code ?? "UNKNOWN", requestId: d.requestId });
      }
      if (!data?.templates) throw Object.assign(new Error("No templates returned"), { code: "NO_TEMPLATES" });

      setTemplates(data.templates as Templates);
      track("outreach_tool_generation_succeeded", { url_host: parsedUrl.hostname });

      setTimeout(() => {
        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      const e = err as { message?: string; code?: string; requestId?: string };
      const detail: ErrorDetail = {
        code: e.code ?? "UNKNOWN",
        message: e.message ?? "Something went wrong.",
        requestId: e.requestId,
        url: trimmedUrl,
        commission: trimmedCommission,
      };
      setError(detail);
      track("outreach_tool_generation_failed", { error_code: detail.code, url_host: parsedUrl.hostname });

      toast({ title: "Generation failed", description: detail.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const displayed = templates ?? (showExample ? EXAMPLE : null);
  const isExample = !templates && showExample;

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-brand-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Rewardful">
            <img src={rewardfulLogo} alt="Rewardful Logo" className="h-7 w-auto" />
          </a>
          <a
            href="https://www.rewardful.com/?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=header_cta"
            className="inline-flex items-center justify-center h-9 px-4 rounded-[5px] bg-brand-blue hover:bg-brand-blue/90 text-white text-sm font-semibold transition-colors"
          >
            Try Rewardful
          </a>
        </div>
      </header>

      <main id="main" role="main" className="flex-1">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-8 text-center">
          <div className="flex flex-col items-center gap-4 mb-5">
            <span className="inline-flex items-center gap-1.5 bg-brand-yellow text-brand-navy text-xs font-semibold px-3 py-1 rounded-full tracking-wide uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Free AI Tool
            </span>
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  onClick={() => track("outreach_tool_video_opened")}
                  aria-label="Watch 4-minute demo"
                  className="group inline-flex items-center gap-3 rounded-lg border border-brand-border bg-white pl-2 pr-4 py-2 shadow-sm hover:shadow-md hover:border-brand-blue/40 transition-all"
                >
                  <span className="relative h-11 w-20 rounded-md overflow-hidden bg-black">
                    <img
                      src="https://img.youtube.com/vi/THDWXuCg068/mqdefault.jpg"
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                      <Play className="h-4 w-4 text-white fill-white" />
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-brand-navy">
                    Watch 4-min demo
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0">
                <DialogHeader className="sr-only">
                  <DialogTitle>Demo video</DialogTitle>
                  <DialogDescription>Affiliate Outreach Generator walkthrough</DialogDescription>
                </DialogHeader>
                <div className="aspect-video w-full">
                  <iframe
                    className="h-full w-full"
                    src="https://www.youtube.com/embed/THDWXuCg068?autoplay=1&rel=0"
                    title="Affiliate Outreach Generator demo"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-brand-navy leading-[1.05]">
            Affiliate Outreach Generator
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Drop your product URL. Get three outreach templates ready to personalize and send — cold email, follow-up, and LinkedIn DM.
          </p>

          {/* Trust strip */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-brand-blue" />
              Generates in ~15s
            </span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-brand-green" />
              No signup required
            </span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-brand-navy" />
              Reads your real homepage
            </span>
          </div>
        </section>

        {/* Generator Form */}
        <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-10">
          <div className="bg-card border border-brand-border rounded-2xl shadow-sm p-6 sm:p-8">
            <form className="space-y-5" onSubmit={handleSubmit} noValidate>
              <div className="space-y-2">
                <Label htmlFor="url" className="text-brand-navy font-medium">
                  Your Product URL
                </Label>
                <Input
                  id="url"
                  type="url"
                  required
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://yourproduct.com"
                  className="h-12"
                  disabled={loading}
                  maxLength={500}
                />
                <p className="text-sm text-muted-foreground">
                  We'll read your homepage so you don't have to describe it.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission" className="text-brand-navy font-medium">
                  Commission Offer
                </Label>
                <Input
                  id="commission"
                  type="text"
                  required
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                  placeholder="e.g. 30% recurring for 12 months"
                  className="h-12"
                  disabled={loading}
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground">
                  Be specific. Affiliates filter by economics first.
                </p>
              </div>




              <div className="flex items-start gap-2">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(v === true)}
                  disabled={loading}
                  className="mt-0.5 h-3.5 w-3.5 [&_svg]:h-3 [&_svg]:w-3"
                />
                <Label htmlFor="consent" className="text-xs font-normal text-muted-foreground leading-snug cursor-pointer">
                  I agree to the{" "}
                  <a href="https://www.rewardful.com/terms" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">Terms</a>
                  {" & "}
                  <a href="https://www.rewardful.com/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">Privacy Policy</a>
                  , and to Rewardful storing the submitted URL.
                </Label>
              </div>

              {error && <ErrorPanel error={error} />}


              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 text-base font-semibold bg-brand-blue hover:bg-brand-blue/90 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  "Generate Templates"
                )}
              </Button>

              {!loading && !templates && (
                <button
                  type="button"
                  onClick={() => {
                    setShowExample((s) => !s);
                    if (!showExample) {
                      setTimeout(() => {
                        document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 50);
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-brand-blue transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  {showExample ? "Hide example output" : "See an example first"}
                </button>
              )}
            </form>

            {/* Loading stages */}
            {loading && (
              <div className="mt-6 pt-6 border-t border-brand-border space-y-2 fade-in">
                {LOADING_STAGES.map((stage, i) => (
                  <div key={stage.label} className="flex items-center gap-2.5 text-sm">
                    {i < stageIdx ? (
                      <Check className="h-4 w-4 text-brand-green shrink-0" />
                    ) : i === stageIdx ? (
                      <Loader2 className="h-4 w-4 text-brand-blue animate-spin shrink-0" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-brand-border shrink-0" />
                    )}
                    <span className={i <= stageIdx ? "text-brand-navy" : "text-muted-foreground"}>
                      {stage.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Results */}
        {displayed && (
          <section id="results" className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
            {isExample && (
              <div className="mb-5 flex items-center justify-between gap-3 px-4 py-3 bg-brand-yellow/40 border border-brand-yellow rounded-xl">
                <p className="text-sm text-brand-navy font-medium">
                  This is an example. Generate your own above for templates tailored to your product.
                </p>
                <button
                  type="button"
                  onClick={() => setShowExample(false)}
                  className="text-sm text-brand-navy/70 hover:text-brand-navy font-medium shrink-0"
                >
                  Hide
                </button>
              </div>
            )}
            <div className="mb-5 flex items-center justify-end">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <Download className="h-4 w-4" />
                    Export
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-1">
                  <button
                    type="button"
                    onClick={() => { track("outreach_tool_exported", { format: "md" }); downloadBlob(`${fileSlug(displayed)}-templates.md`, buildMarkdown(displayed), "text/markdown"); }}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    Markdown (.md)
                  </button>
                  <button
                    type="button"
                    onClick={() => { track("outreach_tool_exported", { format: "txt" }); downloadBlob(`${fileSlug(displayed)}-templates.txt`, buildPlainText(displayed), "text/plain"); }}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    Plain text (.txt)
                  </button>
                  <button
                    type="button"
                    onClick={() => { track("outreach_tool_exported", { format: "pdf" }); track("outreach_tool_pdf_exported"); exportPdf(displayed); }}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-accent text-brand-navy"
                  >
                    PDF (.pdf)
                  </button>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-5">
              {/* Affiliate Intel */}
              {displayed.intel && (
              <div className="bg-card border border-brand-border rounded-2xl p-6 shadow-sm fade-in">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h2 className="text-brand-navy font-semibold text-lg">Affiliate Intel</h2>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-semibold">
                    Deal Score {displayed.intel.dealScore}/10
                  </span>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Product</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.product}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Category</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.category}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground">Commission</dt>
                    <dd className="text-brand-navy font-medium">{displayed.intel.commission}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-muted-foreground mb-1">Ideal Affiliates</dt>
                    <dd>
                      <ul className="space-y-1 list-disc list-inside text-brand-navy/90">
                        {displayed.intel.idealAffiliates.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              </div>
              )}

              {displayed.copyNotes && (
              <div className="bg-brand-yellow/60 border border-brand-yellow rounded-2xl p-6">
                <h2 className="text-brand-navy font-semibold text-lg mb-2">
                  Before You Send
                </h2>
                <p className="text-brand-navy/90 text-sm leading-relaxed">
                  {displayed.copyNotes}
                </p>
              </div>
              )}

              <ResultCard
                icon={<Mail className="h-5 w-5" />}
                label="Cold Email"
                subject={displayed.coldEmail.subject}
                body={displayed.coldEmail.body}
              />
              <ResultCard
                icon={<Reply className="h-5 w-5" />}
                label="Follow-Up"
                note={displayed.followUp.timing || "Send 5–7 days after the first email if no reply"}
                subject={displayed.followUp.subject}
                body={displayed.followUp.body}
              />
              <ResultCard
                icon={<MessageCircle className="h-5 w-5" />}
                label="LinkedIn DM"
                body={displayed.linkedIn.body}
              />
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="bg-brand-navy text-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
              Now Track Every Affiliate You Recruit
            </h2>
            <p className="mt-4 text-white/75 text-lg max-w-2xl mx-auto">
              Templates are step one. Rewardful handles the rest — tracking, commissions, and payouts through Stripe or Paddle. Trusted by 2,800+ SaaS and AI companies.
            </p>
            <a
              href="https://app.getrewardful.com/signup?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=cta_card"
              className="inline-flex items-center justify-center mt-8 h-12 px-7 rounded-[5px] bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold transition-colors"
            >
              Start Your 14-Day Free Trial
            </a>
            <div className="mt-6 flex flex-col items-center gap-3">
              <img
                src={stripePremierPartner}
                alt="Stripe Premier Partner"
                className="h-6 w-auto invert"
              />
              <p className="text-sm text-white/60">
                0% transaction fee · No credit card required
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-brand-border bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>Rewardful — Affiliate Software for SaaS</p>
          <a
            href="https://www.rewardful.com/?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=footer_logo"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="Rewardful"
          >
            <img src={rewardfulLogo} alt="Rewardful Logo" className="h-6 w-auto" />
          </a>
        </div>
      </footer>
    </div>
  );
};

type ResultCardProps = {
  icon: React.ReactNode;
  label: string;
  subject?: string;
  body: string;
  note?: string;
};

const ResultCard = ({ icon, label, subject, body, note }: ResultCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text = subject ? `Subject: ${subject}\n\n${body}` : body;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      const template = label === "Cold Email" ? "cold_email" : label === "Follow-Up" ? "follow_up" : label === "LinkedIn DM" ? "linkedin_dm" : "unknown";
      track("outreach_tool_template_copied", { template, label });
      toast({ title: `${label} copied`, description: "Paste it into your email or DM." });
      setTimeout(() => setCopied(false), 2000);

    } catch {
      toast({ title: "Copy failed", description: "Please copy manually.", variant: "destructive" });
    }
  };

  return (
    <article className="bg-card border border-brand-border rounded-2xl p-6 shadow-sm fade-in">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 text-brand-navy">
          <span className="text-brand-blue">{icon}</span>
          <h3 className="font-semibold">{label}</h3>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-brand-blue transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 text-brand-green" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copy
            </>
          )}
        </button>
      </header>

      {note && (
        <p className="text-xs italic text-muted-foreground mb-3">{note}</p>
      )}

      {subject && (
        <div className="mb-3 px-3 py-2 rounded-md bg-brand-bg border border-brand-border text-sm">
          <span className="text-muted-foreground">Subject: </span>
          <span className="text-brand-navy font-medium">{subject}</span>
        </div>
      )}

      <p className="whitespace-pre-line text-brand-navy/90 leading-relaxed text-[15px]">
        {body}
      </p>
    </article>
  );
};

type ErrorPanelProps = { error: ErrorDetail };

const ErrorPanel = ({ error }: ErrorPanelProps) => {
  const [reportOpen, setReportOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [note, setNote] = useState("");
  const [includeContext, setIncludeContext] = useState(true);

  const detailsText = [
    `Error code: ${error.code}`,
    `Message: ${error.message}`,
    error.requestId && `Request ID: ${error.requestId}`,
    error.url && `URL: ${error.url}`,
    error.commission && `Commission: ${error.commission}`,
    `When: ${new Date().toISOString()}`,
  ].filter(Boolean).join("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(detailsText);
      toast({ title: "Copied", description: "Error details are on your clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Please copy manually.", variant: "destructive" });
    }
  };

  const handleReport = async () => {
    setReporting(true);
    try {
      const payload: Record<string, unknown> = {
        requestId: error.requestId,
        errorCode: error.code,
        errorMessage: error.message,
        userNote: note.trim() || undefined,
      };
      if (includeContext) {
        payload.url = error.url;
        payload.commission = error.commission;
      }
      const { error: fnErr } = await supabase.functions.invoke("report-error", { body: payload });
      if (fnErr) throw new Error(fnErr.message || "Failed to send report");
      setReported(true);
      toast({ title: "Report sent", description: "Thanks — our team will take a look." });
      setTimeout(() => {
        setReportOpen(false);
        setNote("");
        setReported(false);
      }, 1200);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to send report";
      toast({ title: "Couldn't send report", description: msg, variant: "destructive" });
    } finally {
      setReporting(false);
    }
  };

  return (
    <>
      <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-destructive">{error.message}</p>
            <p className="mt-1 text-xs text-muted-foreground font-mono break-all">
              {error.code}
              {error.requestId && ` · ${error.requestId.slice(0, 8)}`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-1.5"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy details
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setReportOpen(true)}
            className="gap-1.5"
          >
            <Send className="h-3.5 w-3.5" />
            Report to team
          </Button>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report this error</DialogTitle>
            <DialogDescription>
              Sends the error details straight to our team. Add a note if you can — it really helps.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="report-note" className="text-brand-navy text-sm">
                What were you trying to do? <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="report-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                placeholder="e.g. tried our pricing page, got an error"
                rows={3}
                disabled={reporting || reported}
              />
            </div>

            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={includeContext}
                onCheckedChange={(v) => setIncludeContext(v === true)}
                disabled={reporting || reported}
                className="mt-0.5"
              />
              <span className="text-brand-navy/90">
                Include the URL and commission I entered (helps us reproduce)
              </span>
            </label>

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-brand-navy">
                What gets sent?
              </summary>
              <pre className="mt-2 p-3 rounded-md bg-muted text-muted-foreground whitespace-pre-wrap break-all">
{`code: ${error.code}
message: ${error.message}${error.requestId ? `\nrequestId: ${error.requestId}` : ""}${includeContext && error.url ? `\nurl: ${error.url}` : ""}${includeContext && error.commission ? `\ncommission: ${error.commission}` : ""}${note.trim() ? `\nnote: ${note.trim()}` : ""}`}
              </pre>
            </details>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReportOpen(false)}
              disabled={reporting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleReport}
              disabled={reporting || reported}
              className="bg-brand-blue hover:bg-brand-blue/90 text-white gap-1.5"
            >
              {reported ? (
                <>
                  <Check className="h-4 w-4" />
                  Sent
                </>
              ) : reporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Send report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Index;

